import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { read, utils } from "xlsx";
import DOMPurify from "dompurify";
import { Loader2, AlertCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** The sheet on screen: 1-based, as comment anchors count them. */
export interface ActiveSheetInfo {
  index: number;
  name: string;
  count: number;
}

export interface XlsxRendererHandle {
  /** Show the nth sheet (1-based). Out-of-range numbers are ignored. */
  showSheet: (sheetNumber: number) => void;
}

interface XlsxRendererProps {
  blobData: Blob;
  filename: string;
  onDownload?: () => void;
  /** 1-based sheet to open on, once the workbook has parsed. */
  initialSheet?: number;
  /** Fired when the workbook parses and whenever the reader changes sheet —
   *  the comments rail anchors a new comment to it. */
  onActiveSheetChange?: (sheet: ActiveSheetInfo) => void;
  /** Fired once the workbook has parsed and rendered (view analytics). */
  onLoad?: () => void;
}

interface SheetData {
  name: string;
  html: string;
}

/**
 * Renders .xlsx/.xls files inline using SheetJS.
 * Converts the spreadsheet to styled HTML tables with sheet tabs for multi-sheet workbooks.
 */
export const XlsxRenderer = forwardRef<XlsxRendererHandle, XlsxRendererProps>(function XlsxRenderer(
  { blobData, filename, onDownload, initialSheet, onActiveSheetChange, onLoad },
  ref
) {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const initialSheetRef = useRef(initialSheet);
  initialSheetRef.current = initialSheet;

  useImperativeHandle(
    ref,
    () => ({
      showSheet: (sheetNumber: number) => {
        setActiveSheet((current) =>
          sheetNumber >= 1 && sheetNumber <= sheets.length ? sheetNumber - 1 : current
        );
      },
    }),
    [sheets.length]
  );

  // Latest callback without re-firing the report when the parent re-renders.
  const onActiveSheetChangeRef = useRef(onActiveSheetChange);
  onActiveSheetChangeRef.current = onActiveSheetChange;
  useEffect(() => {
    const sheet = sheets[activeSheet];
    if (!sheet) return;
    onActiveSheetChangeRef.current?.({
      index: activeSheet + 1,
      name: sheet.name,
      count: sheets.length,
    });
  }, [sheets, activeSheet]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      if (cancelled) return;

      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = read(data, { type: "array" });

        const sheetData: SheetData[] = workbook.SheetNames.map((name) => {
          const worksheet = workbook.Sheets[name];
          const html = utils.sheet_to_html(worksheet, { id: `sheet-${name}` });
          return { name, html };
        });

        if (!cancelled) {
          setSheets(sheetData);
          const wanted = initialSheetRef.current;
          setActiveSheet(
            wanted !== undefined && wanted >= 1 && wanted <= sheetData.length ? wanted - 1 : 0
          );
          setIsLoading(false);
          onLoad?.();
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[XlsxRenderer] Failed to parse:", err);
          setError(
            "Failed to render spreadsheet. The file may be corrupted or in an unsupported format."
          );
          setIsLoading(false);
        }
      }
    };

    reader.onerror = () => {
      if (!cancelled) {
        setError("Failed to read file.");
        setIsLoading(false);
      }
    };

    reader.readAsArrayBuffer(blobData);

    return () => {
      cancelled = true;
    };
  }, [blobData, onLoad]);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-muted-foreground">
        <AlertCircle className="h-12 w-12 text-destructive/50" />
        <p className="max-w-md text-center text-sm">{error}</p>
        {onDownload && (
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download {filename}
          </Button>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Rendering spreadsheet...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Sheet tabs (only show if multiple sheets) */}
      {sheets.length > 1 && (
        <div className="flex shrink-0 gap-0 border-b bg-muted/30">
          {sheets.map((sheet, index) => (
            <button
              key={sheet.name}
              type="button"
              className={cn(
                "border-r px-3 py-1.5 text-xs font-medium transition-colors",
                index === activeSheet
                  ? "bg-background text-foreground"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              )}
              onClick={() => setActiveSheet(index)}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}

      {/* Sheet content */}
      <div className="flex-1 overflow-auto bg-white">
        {sheets[activeSheet] && (
          <div
            className="xlsx-preview-content p-2"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sheets[activeSheet].html) }}
          />
        )}
      </div>
    </div>
  );
});
